/* eslint-disable react-refresh/only-export-components */
import { RotateCcw, Save } from "lucide-react";
import type { RaRecord } from "ra-core";
import {
  EditBase,
  Form,
  useCanAccess,
  useGetList,
  useInput,
  useNotify,
  useTranslate,
} from "ra-core";
import { useCallback, useMemo } from "react";
import { Link } from "react-router";
import { useFormContext } from "react-hook-form";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { toSlug } from "@/lib/toSlug";
import { ArrayInput } from "@/components/admin/array-input";
import { AutocompleteInput } from "@/components/admin/autocomplete-input";
import { SimpleFormIterator } from "@/components/admin/simple-form-iterator";
import { TextInput } from "@/components/admin/text-input";

import { PilotReportsBadge } from "../feedback/PilotReportsBadge";
import ImageEditorField from "../misc/ImageEditorField";
import {
  useConfigurationContext,
  useConfigurationUpdater,
  type ConfigurationContextValue,
} from "../root/ConfigurationContext";
import { defaultConfiguration } from "../root/defaultConfiguration";
import { UnclassifiedNavLinks } from "../whatsapp/UnclassifiedNavLinks";

const SECTIONS = [
  {
    id: "branding",
    label: "crm.settings.sections.branding",
    fallback: "Branding",
  },
  {
    id: "companies",
    label: "resources.companies.name",
    fallback: "Companies",
  },
  { id: "deals", label: "resources.deals.name", fallback: "Deals" },
  { id: "notes", label: "resources.notes.name", fallback: "Notes" },
  { id: "tasks", label: "resources.tasks.name", fallback: "Tasks" },
];

/** Ensure every item in a { value, label } array has a value (slug from label). */
const ensureValues = (items: { value?: string; label: string }[] | undefined) =>
  items?.map((item) => ({ ...item, value: item.value || toSlug(item.label) }));

type ValidateItemsInUseMessages = {
  duplicate?: (displayName: string, duplicates: string[]) => string;
  inUse?: (displayName: string, inUse: string[]) => string;
  validating?: string;
};

/**
 * Validate that no items were removed if they are still referenced by existing deals.
 * Also rejects duplicate slug values.
 * Returns undefined if valid, or an error message string.
 */
export const validateItemsInUse = (
  items: { value: string; label: string }[] | undefined,
  deals: RaRecord[] | undefined,
  fieldName: string,
  displayName: string,
  messages?: ValidateItemsInUseMessages,
) => {
  if (!items) return undefined;
  // Check for duplicate slugs
  const slugs = items.map((i) => i.value || toSlug(i.label));
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  for (const slug of slugs) {
    if (seen.has(slug)) duplicates.add(slug);
    seen.add(slug);
  }
  if (duplicates.size > 0) {
    const duplicatesList = [...duplicates];
    return (
      messages?.duplicate?.(displayName, duplicatesList) ??
      `Duplicate ${displayName}: ${duplicatesList.join(", ")}`
    );
  }
  // Check that no in-use value was removed (skip if deals haven't loaded)
  if (!deals) return messages?.validating ?? "Validating…";
  const values = new Set(slugs);
  const inUse = [
    ...new Set(
      deals
        .filter(
          (deal) => deal[fieldName] && !values.has(deal[fieldName] as string),
        )
        .map((deal) => deal[fieldName] as string),
    ),
  ];
  if (inUse.length > 0) {
    return (
      messages?.inUse?.(displayName, inUse) ??
      `Cannot remove ${displayName} that are still used by deals: ${inUse.join(", ")}`
    );
  }
  return undefined;
};

const getCurrencyChoices = () => {
  const displayNames = new Intl.DisplayNames(
    typeof navigator !== "undefined"
      ? (navigator.languages as string[])
      : ["en"],
    { type: "currency" },
  );
  return Intl.supportedValuesOf("currency").map((code) => ({
    id: code,
    name: `${code} – ${displayNames.of(code)}`,
  }));
};

const transformFormValues = (data: Record<string, any>) => ({
  config: {
    title: data.title,
    lightModeLogo: data.lightModeLogo,
    darkModeLogo: data.darkModeLogo,
    currency: data.currency,
    companySectors: ensureValues(data.companySectors),
    dealCategories: ensureValues(data.dealCategories),
    taskTypes: ensureValues(data.taskTypes),
    dealStages: ensureValues(data.dealStages),
    dealPipelineStatuses: data.dealPipelineStatuses,
    noteStatuses: ensureValues(data.noteStatuses),
  } as ConfigurationContextValue,
});

export const SettingsPage = () => {
  const updateConfiguration = useConfigurationUpdater();
  const notify = useNotify();

  return (
    <EditBase
      resource="configuration"
      id={1}
      mutationMode="pessimistic"
      redirect={false}
      transform={transformFormValues}
      mutationOptions={{
        onSuccess: (data: any) => {
          updateConfiguration(data.config);
          notify("crm.settings.saved");
        },
        onError: () => {
          notify("crm.settings.save_error", {
            type: "error",
          });
        },
      }}
    >
      <SettingsForm />
    </EditBase>
  );
};

SettingsPage.path = "/settings";

const SettingsForm = () => {
  const config = useConfigurationContext();

  const defaultValues = useMemo(
    () => ({
      title: config.title,
      lightModeLogo: { src: config.lightModeLogo },
      darkModeLogo: { src: config.darkModeLogo },
      currency: config.currency,
      companySectors: config.companySectors,
      dealCategories: config.dealCategories,
      taskTypes: config.taskTypes,
      dealStages: config.dealStages,
      dealPipelineStatuses: config.dealPipelineStatuses,
      noteStatuses: config.noteStatuses,
    }),
    [config],
  );

  return (
    <Form defaultValues={defaultValues}>
      <SettingsFormFields />
    </Form>
  );
};

/**
 * Almikia Epic EP (TASK-105): the "Etapas" section stopped saying stages are
 * locked -- direccion (who can now relabel/reorder/archive them on its own
 * screen) sees a link there instead; every other role still sees the
 * configured labels read-only, with a note that Dirección configures them
 * (mirrors `canAccess("deal_stage_configs", "edit")`, the same gate the
 * "Etapas" screen and rail link use).
 */
const DealStagesConfigLink = ({
  dealStages,
}: {
  dealStages: { value: string; label?: string }[];
}) => {
  const translate = useTranslate();
  const { canAccess, isPending } = useCanAccess({
    resource: "deal_stage_configs",
    action: "edit",
  });

  if (isPending) return null;

  if (canAccess) {
    return (
      <Button asChild variant="outline" size="sm">
        <Link to="/deal_stage_configs">
          {translate("crm.settings.deals.stages_manage_link")}
        </Link>
      </Button>
    );
  }

  return (
    <>
      <p className="text-sm text-muted-foreground">
        {translate("crm.settings.deals.stages_readonly_note")}
      </p>
      <div className="flex flex-wrap gap-2">
        {dealStages.map((stage) => (
          <Badge key={stage.value} variant="secondary">
            {stage.label || stage.value}
          </Badge>
        ))}
      </div>
    </>
  );
};

/**
 * Almikia Epic EA (TASK-113): the "Actividades" section -- direccion-only
 * entry point, nothing shown to any other role (mirrors
 * `canAccess("deal_activity_types", "edit")`, the same gate the "Actividades"
 * screen and rail link use). Unlike `DealStagesConfigLink`, there is no
 * read-only fallback: the ticket's own acceptance criterion is that every
 * other role has no entry point at all.
 */
const ActivityCatalogueLink = () => {
  const translate = useTranslate();
  const { canAccess, isPending } = useCanAccess({
    resource: "deal_activity_types",
    action: "edit",
  });

  if (isPending || !canAccess) return null;

  return (
    <>
      <Separator />
      <h3 className="text-lg font-medium text-muted-foreground">
        {translate("resources.deal_activity_types.name", { smart_count: 2 })}
      </h3>
      <Button asChild variant="outline" size="sm">
        <Link to="/deal_activity_types">
          {translate("crm.settings.deals.activities_manage_link")}
        </Link>
      </Button>
    </>
  );
};

/**
 * Almikia Epic EF (TASK-121): the "Opciones del perfil" section --
 * direccion-only entry point (mirrors `canAccess("profile_buyer_types",
 * "edit")`, the same gate the screen itself uses); no read-only fallback,
 * same reasoning as `ActivityCatalogueLink` -- every other role has no entry
 * point at all.
 */
const ProfileOptionsConfigLink = () => {
  const translate = useTranslate();
  const { canAccess, isPending } = useCanAccess({
    resource: "profile_buyer_types",
    action: "edit",
  });

  if (isPending || !canAccess) return null;

  return (
    <>
      <Separator />
      <h3 className="text-lg font-medium text-muted-foreground">
        {translate("crm.profileOptions.page_title")}
      </h3>
      <Button asChild variant="outline" size="sm">
        <Link to="/profile_options">
          {translate("crm.settings.deals.profile_options_manage_link")}
        </Link>
      </Button>
    </>
  );
};

/**
 * Almikia Epic EC2d (TASK-138): "Tipos de propiedad" -- direccion-only entry point (mirrors
 * `canAccess("property_types", "edit")`, the gate of the screen itself); no entry for other roles.
 */
const PropertyTypesConfigLink = () => {
  const translate = useTranslate();
  const { canAccess, isPending } = useCanAccess({
    resource: "property_types",
    action: "edit",
  });

  if (isPending || !canAccess) return null;

  return (
    <>
      <Separator />
      <h3 className="text-lg font-medium text-muted-foreground">
        {translate("crm.propertyTypes.page_title")}
      </h3>
      <Button asChild variant="outline" size="sm">
        <Link to="/property_types">
          {translate("crm.propertyTypes.manage_link")}
        </Link>
      </Button>
    </>
  );
};

/**
 * Almikia Epic E2b (TASK-022): "Orígenes de leads" (direccion only, mirrors
 * the screen's own `lead_sources`/`edit` gate); the Colegas link was removed in EC2a
 * (colegas are contacts of type Colega now). No entry at all for any other role.
 */
const LeadCataloguesLinks = () => {
  const translate = useTranslate();
  const sources = useCanAccess({ resource: "lead_sources", action: "edit" });
  // Almikia Epic EL2 (TASK-074): "Guardias y asignación" (Team Leader, asesora of the project,
  // direccion, bpo lead_manager; the screen itself re-checks per project).
  const guardias = useCanAccess({
    resource: "project_carousel",
    action: "list",
  });
  // Almikia Epic PL (TASK-062): "Reportes del piloto" (direccion only).
  const pilotReports = useCanAccess({
    resource: "feedback_reports_summary",
    action: "list",
  });
  // Almikia Epic E9 (TASK-049): "Leads de Meta" (direccion only).
  const metaLeads = useCanAccess({
    resource: "meta_webhook_events",
    action: "list",
  });
  // Almikia Epic EM (TASK-069): "Eventos Meta" (direccion only).
  const metaEvents = useCanAccess({
    resource: "meta_capi_events_summary",
    action: "list",
  });
  // Almikia Epic EW2 (TASK-080): "WhatsApp" settings and counter (direccion only).
  const whatsapp = useCanAccess({
    resource: "whatsapp_settings",
    action: "list",
  });

  // Almikia Epic EN (TASK-091): "Avisos internos" settings (direccion only).
  const internalNotices = useCanAccess({
    resource: "internal_notices_settings",
    action: "list",
  });

  return (
    <>
      {!whatsapp.isPending && whatsapp.canAccess ? (
        <>
          <Separator />
          <h3 className="text-lg font-medium text-muted-foreground">
            {translate("crm.whatsapp.settings.nav")}
          </h3>
          <Button asChild variant="outline" size="sm">
            <Link to="/whatsapp_settings">
              {translate("crm.whatsapp.settings.manage_link")}
            </Link>
          </Button>
        </>
      ) : null}
      {/* Almikia Epic EW3 (TASK-095): only the holder of the classify permission sees these. */}
      <UnclassifiedNavLinks withHeading />
      {!internalNotices.isPending && internalNotices.canAccess ? (
        <>
          <Separator />
          <h3 className="text-lg font-medium text-muted-foreground">
            {translate("crm.internalNotices.settings.nav")}
          </h3>
          <Button asChild variant="outline" size="sm" className="min-h-11">
            <Link to="/internal_notices_settings">
              {translate("crm.internalNotices.settings.manage_link")}
            </Link>
          </Button>
        </>
      ) : null}
      {!sources.isPending && sources.canAccess ? (
        <>
          <Separator />
          <h3 className="text-lg font-medium text-muted-foreground">
            {translate("crm.leadSources.page_title")}
          </h3>
          <Button asChild variant="outline" size="sm">
            <Link to="/lead_sources">
              {translate("crm.leadSources.manage_link")}
            </Link>
          </Button>
        </>
      ) : null}
      {!guardias.isPending && guardias.canAccess ? (
        <>
          <Separator />
          <h3 className="text-lg font-medium text-muted-foreground">
            {translate("resources.guardias.nav")}
          </h3>
          <Button asChild variant="outline" size="sm">
            <Link to="/guardias">{translate("resources.guardias.nav")}</Link>
          </Button>
        </>
      ) : null}
      {!metaLeads.isPending && metaLeads.canAccess ? (
        <>
          <Separator />
          <h3 className="text-lg font-medium text-muted-foreground">
            {translate("crm.metaLeads.title")}
          </h3>
          <Button asChild variant="outline" size="sm">
            <Link to="/meta_leads">
              {translate("crm.metaLeads.manage_link")}
            </Link>
          </Button>
        </>
      ) : null}
      {!metaEvents.isPending && metaEvents.canAccess ? (
        <>
          <Separator />
          <h3 className="text-lg font-medium text-muted-foreground">
            {translate("crm.metaOrigin.events.title")}
          </h3>
          <Button asChild variant="outline" size="sm">
            <Link to="/meta_events">
              {translate("crm.metaOrigin.events.manage_link")}
            </Link>
          </Button>
        </>
      ) : null}
      {!pilotReports.isPending && pilotReports.canAccess ? (
        <>
          <Separator />
          <h3 className="text-lg font-medium text-muted-foreground">
            {translate("crm.pilotReports.nav")}
            <PilotReportsBadge />
          </h3>
          <Button asChild variant="outline" size="sm">
            <Link to="/pilot_reports">{translate("crm.pilotReports.nav")}</Link>
          </Button>
        </>
      ) : null}
    </>
  );
};

const SettingsFormFields = () => {
  const translate = useTranslate();
  const currencyChoices = useMemo(() => getCurrencyChoices(), []);
  // Deal stages and pipeline statuses are fixed business states referenced by
  // the database POSI gate (Almikia Epic E2, TASK-011): read them from
  // configuration for a read-only display, never from form state.
  const { dealStages, dealPipelineStatuses } = useConfigurationContext();
  const {
    reset,
    formState: { isSubmitting },
  } = useFormContext();

  const categoryDisplayName = translate(
    "crm.settings.validation.entities.categories",
  );

  const { data: deals } = useGetList("deals", {
    pagination: { page: 1, perPage: 1000 },
  });

  const validateDealCategories = useCallback(
    (categories: { value: string; label: string }[] | undefined) =>
      validateItemsInUse(categories, deals, "category", categoryDisplayName, {
        duplicate: (displayName, duplicates) =>
          translate("crm.settings.validation.duplicate", {
            display_name: displayName,
            items: duplicates.join(", "),
          }),
        inUse: (displayName, inUse) =>
          translate("crm.settings.validation.in_use", {
            display_name: displayName,
            items: inUse.join(", "),
          }),
        validating: translate("crm.settings.validation.validating"),
      }),
    [categoryDisplayName, deals, translate],
  );

  return (
    <div className="flex gap-8 mt-4 pb-20">
      {/* Left navigation */}
      <nav className="hidden md:block w-48 shrink-0">
        <div className="sticky top-4 space-y-1">
          <h1 className="text-2xl font-semibold px-3 mb-2">
            {translate("crm.settings.title")}
          </h1>
          {SECTIONS.map((section) => (
            <button
              key={section.id}
              type="button"
              onClick={() => {
                document
                  .getElementById(section.id)
                  ?.scrollIntoView({ behavior: "smooth" });
              }}
              className="block w-full text-left px-3 py-1 text-sm rounded-md hover:text-foreground hover:bg-muted transition-colors"
            >
              {translate(section.label, { smart_count: 2 })}
            </button>
          ))}
        </div>
      </nav>

      {/* Main content */}
      <div className="flex-1 min-w-0 max-w-2xl space-y-6">
        {/* Branding */}
        <Card id="branding">
          <CardContent className="space-y-4">
            <h2 className="text-xl font-semibold text-muted-foreground">
              {translate("crm.settings.sections.branding")}
            </h2>
            <TextInput source="title" label="crm.settings.app_title" />
            <div className="flex gap-8">
              <div className="flex flex-col items-center gap-1">
                <p className="text-sm text-muted-foreground">
                  {translate("crm.settings.light_mode_logo")}
                </p>
                <ImageEditorField
                  source="lightModeLogo"
                  width={100}
                  height={100}
                  linkPosition="bottom"
                  backgroundImageColor="#f5f5f5"
                />
              </div>
              <div className="flex flex-col items-center gap-1">
                <p className="text-sm text-muted-foreground">
                  {translate("crm.settings.dark_mode_logo")}
                </p>
                <ImageEditorField
                  source="darkModeLogo"
                  width={100}
                  height={100}
                  linkPosition="bottom"
                  backgroundImageColor="#1a1a1a"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Companies */}
        <Card id="companies">
          <CardContent className="space-y-4">
            <h2 className="text-xl font-semibold text-muted-foreground">
              {translate("resources.companies.name", {
                smart_count: 2,
              })}
            </h2>
            <h3 className="text-lg font-medium text-muted-foreground">
              {translate("crm.settings.companies.sectors")}
            </h3>
            <ArrayInput
              source="companySectors"
              label={false}
              helperText={false}
            >
              <SimpleFormIterator disableReordering disableClear>
                <TextInput source="label" label={false} />
              </SimpleFormIterator>
            </ArrayInput>
          </CardContent>
        </Card>

        {/* Deals */}
        <Card id="deals">
          <CardContent className="space-y-4">
            <h2 className="text-xl font-semibold text-muted-foreground">
              {translate("resources.deals.name", {
                smart_count: 2,
              })}
            </h2>
            <h3 className="text-lg font-medium text-muted-foreground">
              {translate("crm.settings.deals.currency")}
            </h3>
            <AutocompleteInput
              source="currency"
              label={false}
              choices={currencyChoices}
              inputText={(choice) => choice?.id}
              modal
            />

            <Separator />

            {/* Deal stages and pipeline statuses are fixed business states
                referenced by the database POSI gate (Almikia Epic E2): they
                are shown read-only, never editable, unlike the other
                settings below. */}
            <h3 className="text-lg font-medium text-muted-foreground">
              {translate("crm.settings.deals.stages")}
            </h3>
            <DealStagesConfigLink dealStages={dealStages} />

            <ActivityCatalogueLink />

            <ProfileOptionsConfigLink />

            <PropertyTypesConfigLink />

            <LeadCataloguesLinks />

            <Separator />

            <h3 className="text-lg font-medium text-muted-foreground">
              {translate("crm.settings.deals.pipeline_statuses")}
            </h3>
            <p className="text-sm text-muted-foreground">
              {translate("crm.settings.deals.stages_locked")}
            </p>
            <div className="flex flex-wrap gap-2">
              {dealPipelineStatuses.map((value) => {
                const stage = dealStages.find((s) => s.value === value);
                return (
                  <Badge key={value} variant="secondary">
                    {stage?.label || value}
                  </Badge>
                );
              })}
            </div>

            <Separator />

            <h3 className="text-lg font-medium text-muted-foreground">
              {translate("crm.settings.deals.categories")}
            </h3>
            <ArrayInput
              source="dealCategories"
              label={false}
              helperText={false}
              validate={validateDealCategories}
            >
              <SimpleFormIterator disableReordering disableClear>
                <TextInput source="label" label={false} />
              </SimpleFormIterator>
            </ArrayInput>
          </CardContent>
        </Card>

        {/* Notes */}
        <Card id="notes">
          <CardContent className="space-y-4">
            <h2 className="text-xl font-semibold text-muted-foreground">
              {translate("resources.notes.name", {
                smart_count: 2,
              })}
            </h2>
            <h3 className="text-lg font-medium text-muted-foreground">
              {translate("crm.settings.notes.statuses")}
            </h3>
            <ArrayInput source="noteStatuses" label={false} helperText={false}>
              <SimpleFormIterator inline disableReordering disableClear>
                <TextInput source="label" label={false} className="flex-1" />
                <ColorInput source="color" />
              </SimpleFormIterator>
            </ArrayInput>
          </CardContent>
        </Card>

        {/* Tasks */}
        <Card id="tasks">
          <CardContent className="space-y-4">
            <h2 className="text-xl font-semibold text-muted-foreground">
              {translate("resources.tasks.name", {
                smart_count: 2,
              })}
            </h2>
            <h3 className="text-lg font-medium text-muted-foreground">
              {translate("crm.settings.tasks.types")}
            </h3>
            <ArrayInput source="taskTypes" label={false} helperText={false}>
              <SimpleFormIterator disableReordering disableClear>
                <TextInput source="label" label={false} />
              </SimpleFormIterator>
            </ArrayInput>
          </CardContent>
        </Card>
      </div>

      {/* Sticky save button */}
      <div className="fixed bottom-0 left-0 right-0 border-t bg-background p-4">
        <div className="max-w-screen-xl mx-auto flex gap-8 px-4">
          <div className="hidden md:block w-48 shrink-0" />
          <div className="flex-1 min-w-0 max-w-2xl flex justify-between">
            <Button
              type="button"
              variant="ghost"
              onClick={() =>
                reset({
                  ...defaultConfiguration,
                  lightModeLogo: {
                    src: defaultConfiguration.lightModeLogo,
                  },
                  darkModeLogo: { src: defaultConfiguration.darkModeLogo },
                })
              }
            >
              <RotateCcw className="h-4 w-4 mr-1" />
              {translate("crm.settings.reset_defaults")}
            </Button>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => window.history.back()}
              >
                {translate("ra.action.cancel")}
              </Button>
              {/* design-system.md section 6: the screen's single main
                  action opts into the gradient primary style; the Reset
                  and Cancel buttons above stay ghost/outline. */}
              <Button type="submit" variant="gradient" disabled={isSubmitting}>
                <Save className="h-4 w-4 mr-1" />
                {isSubmitting
                  ? translate("crm.settings.saving")
                  : translate("ra.action.save")}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/** A minimal color picker input compatible with ra-core's useInput. */
const ColorInput = ({ source }: { source: string }) => {
  const { field } = useInput({ source });
  return (
    <input
      type="color"
      {...field}
      value={field.value || "#000000"}
      className="w-9 h-9 shrink-0 cursor-pointer appearance-none rounded border bg-transparent p-0.5 [&::-webkit-color-swatch-wrapper]:cursor-pointer [&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch]:cursor-pointer [&::-webkit-color-swatch]:rounded-sm [&::-webkit-color-swatch]:border-none [&::-moz-color-swatch]:cursor-pointer [&::-moz-color-swatch]:rounded-sm [&::-moz-color-swatch]:border-none"
    />
  );
};
